import AtriumApi from "api";
import DarkButton from "components/actions/DarkButton";
import GrayButton from "components/actions/GrayButton";
import TransparentButton from "components/actions/TransparentButton";
import AlertPane from "components/alert";
import InputField from "components/fields/InputField";
import BackIcon from "components/icons/BackIcon";
import CheckIcon from "components/icons/Check";
import StatusLabel from "components/label/StatusLabel";
import { useState } from "react";
import toast from "react-hot-toast";

const TwofactorConfigurator = ({ back, user, type }) => {
  const [step, setStep] = useState("send");
  const sendVerificationCode = () => {
    setStep("verification");
  };

  const verifyCode = () => {};

  return (
    <>
      <div className="w-full rounded-[30px] pt-[30px]  gap-[30px]">
        <GrayButton onClick={back}>
          <span className="inline-block align-middle mr-2">
            <BackIcon />
          </span>
          <span className="inline-block align-middle">Back</span>
        </GrayButton>

        <div className="mt-6">
          <h1 className="font-bold text-2xl inline-block mr-2">
            Two-factor authentication via {type}
          </h1>
          <StatusLabel
            status={
              type === "email"
                ? user.backupEmail
                  ? "enabled"
                  : "disabled"
                : user.backupMobile
                ? "enabled"
                : "disabled"
            }
          />
        </div>
      </div>

      <div className="w-full max-w-[960px] block md:flex flex-row relative items-start rounded-[15px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
        <div className="flex-1 mb-6">
          <h2 className="font-bold text-lg mb-2">
            {type === "email" ? "Email" : "Mobile"} authentication
          </h2>
          <p className="text-gray-700 text-sm">
            You will be prompted to enter a code sent to your {type} every time
            you login.
          </p>
        </div>

        <div className="flex-2">
          <InputField
            variant="profile_form"
            label={
              type === "email"
                ? step === "send"
                  ? "Email address"
                  : "Your email"
                : step === "send"
                ? "Mobile number"
                : "Your mobile number"
            }
            placeholder=""
            id="two-factor-email"
            type="text"
            extra="w-full pb-4 flex flex-col"
            value={type === "email" ? user.backupEmail : user.backupMobile}
            disabled={step !== "send"}
          />

          {step === "verification" && (
            <div className="bg-brand-100 rounded-2xl p-4 mb-4">
              <div className="inline-block align-middle bg-brand-500 text-white mr-2 rounded-full">
                <CheckIcon />
              </div>
              <span className="align-middle text-brand-700">
                Verification code sent to{" "}
                <b>{type === "email" ? user.backupEmail : user.backupMobile}</b>
              </span>
            </div>
          )}

          {step === "verification" && (
            <InputField
              variant="profile_form"
              label="Please enter received code below to confirm"
              placeholder="Enter code"
              id="verification-code"
              type="text"
              extra="w-full pb-4 flex flex-col"
              value={user.code || ""}
            />
          )}

          {step === "send" && (
            <DarkButton onClick={sendVerificationCode}>
              Send verification code
            </DarkButton>
          )}
          {step === "verification" && (
            <DarkButton onClick={verifyCode}>Confirm</DarkButton>
          )}
        </div>
      </div>
    </>
  );
};

const SecurityForm = (props) => {
  const [user] = useState(props.user || {});
  const [page, setPage] = useState("password");
  const [passwords, setPasswords] = useState({});
  const [errors, setErrors] = useState([]);
  const [notice, setNotice] = useState("");

  const onUpdatePassword = async (e) => {
    e.preventDefault();

    let newErrors = [];

    if (!passwords.old) {
      newErrors.push({ path: "old", message: "Current password is required" });
    }

    if (!passwords.new) {
      newErrors.push({ path: "new", message: "New password is required" });
    }

    if (passwords.new && passwords.old && passwords.new === passwords.old) {
      newErrors.push({
        path: "new",
        message: "You are using old password as new one",
      });
    }

    if (!passwords.confirm) {
      newErrors.push({
        path: "confirm",
        message: "Please confirm password",
      });
    }

    if (
      passwords.new &&
      passwords.confirm &&
      passwords.new !== passwords.confirm
    ) {
      newErrors.push({
        path: "confirm",
        message: "Password is mismatched",
      });
    }

    if (newErrors.length > 0) {
      setErrors(newErrors);
      newErrors.forEach((err) => toast.error(err.message));
      return;
    }

    // All validations passed — now disable the button and send API request
    setErrors([]);
    const submitButton = e.target.querySelector('button[type="submit"]');
    submitButton.setAttribute("disabled", "");

    try {
      const resp = await AtriumApi.put("/v1/profile", {
        profile: { oldPassword: passwords.old, password: passwords.new },
      });
      console.log("response", resp);

      if (resp.status === 422) {
        toast.error(resp.data.message);
      } else {
        setNotice("Your password has updated successfully");
      }
    } catch (error) {
      toast.error(error?.data?.message || "An error occurred.");
    } finally {
      submitButton.removeAttribute("disabled");
    }
  };

  return (
    <>
      {page === "2fa-email" && (
        <TwofactorConfigurator
          back={() => setPage("password")}
          user={user}
          type="email"
        />
      )}
      {page === "2fa-mobile" && (
        <TwofactorConfigurator
          back={() => setPage("password")}
          user={user}
          type="mobile phone"
        />
      )}

      {page === "password" && (
        <>
          <form
            onSubmit={onUpdatePassword}
            className="w-full block md:flex flex-row relative items-start rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5"
          >
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">Change password</h2>
              <p className="text-gray-700 text-sm">Change your password</p>
            </div>

            <div className="flex-2">
              {notice && <AlertPane state="success">{notice}</AlertPane>}
              {errors.length ? (
                <AlertPane state="error">{errors[0].message}</AlertPane>
              ) : (
                ""
              )}
              <InputField
                variant="profile_form"
                label="Old password"
                placeholder="Please enter your old password"
                id="old-password"
                type="password"
                extra="w-full pb-4 flex flex-col"
                value={passwords.old || ""}
                state={
                  errors.findIndex((error) => error.path === "old") !== -1 &&
                  "error"
                }
                onChange={(e) =>
                  setPasswords({ ...passwords, old: e.target.value })
                }
              />

              <InputField
                variant="profile_form"
                label="New password"
                placeholder="Please enter your new password"
                id="new-password"
                type="password"
                extra="w-full pb-4 flex flex-col"
                value={passwords.new || ""}
                state={
                  errors.findIndex((error) => error.path === "new") !== -1 &&
                  "error"
                }
                onChange={(e) =>
                  setPasswords({ ...passwords, new: e.target.value })
                }
              />

              <InputField
                variant="profile_form"
                label="Confirm new password"
                placeholder="Please confirm your new password"
                id="confirm-password"
                type="password"
                extra="w-full pb-4 flex flex-col"
                value={passwords.confirm || ""}
                state={
                  errors.findIndex((error) => error.path === "confirm") !==
                    -1 && "error"
                }
                onChange={(e) =>
                  setPasswords({ ...passwords, confirm: e.target.value })
                }
              />

              <DarkButton type="submit">Save password</DarkButton>
            </div>
          </form>

          <div className="w-full block md:flex flex-row  relative items-start rounded-[30px] p-3 md:p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none mb-5">
            <div className="flex-1 mb-6">
              <h2 className="font-bold text-lg mb-2">2FA Authentication</h2>
              <p className="text-gray-700 text-sm">
                Enable two-factor authentication for added security.
              </p>
            </div>

            <div className="flex-2 whitespace-nowrap">
              <div className="flex flex-row gap-6 border-b border-gray-200 dark:border-gray-700 pb-4 items-center">
                <p className="font-bold text-base flex-1">Via mobile</p>
                <p className="text-gray-700">
                  {user.backupMobile ? user.backupMobile : "Not configured"}
                </p>
                <TransparentButton
                  customClass="text-[14px] h-[34px] md:h-[38px] md:px-4"
                  onClick={() => setPage("2fa-mobile")}
                >
                  Setup
                </TransparentButton>
              </div>

              <div className="flex flex-row gap-6 pt-4 items-center">
                <p className="font-bold text-base flex-1">Via email</p>
                <p className="text-gray-700">
                  {user.backupEmail ? user.backupEmail : "Not configured"}
                </p>
                <TransparentButton
                  customClass="text-[14px] h-[34px] md:h-[38px] md:px-4"
                  onClick={() => setPage("2fa-email")}
                >
                  Setup
                </TransparentButton>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default SecurityForm;
